import { useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Fab,
  Zoom,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import {
  useAuth,
  DELIVERY_WINDOWS,
  DeliveryWindowKey,
  parseShiftWindows,
  toDateKey,
  getDayOfWeekName,
  formatShortCalendarDateLabel,
  getScheduleShiftForDate,
  isScheduleShiftMarkedOff,
} from '../context/AuthContext';

const getWindowIcon = (key: DeliveryWindowKey) => {
  if (key === 'morning') return <WbSunnyOutlinedIcon sx={{ fontSize: 16, color: '#f57c00' }} />;
  if (key === 'afternoon') return <LightModeOutlinedIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
  return <NightsStayOutlinedIcon sx={{ fontSize: 16, color: '#5e35b1' }} />;
};

export default function DriverSchedule() {
  const { currentUser, schedules } = useAuth();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<'week' | 'month'>('month');
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!currentUser) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '220px',
          bgcolor: 'grey.50',
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <LockOutlinedIcon color="primary" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              SDS Driver Sign-In Required
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Please log in with your Success Delivery Services credentials to view your assigned delivery schedule.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const mySchedule = schedules.find((s) => s.driverId === currentUser.id);

  const getVisibleDates = (): Date[] => {
    if (viewMode === 'week') {
      const d = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate());
      const sunday = new Date(d);
      sunday.setDate(d.getDate() - d.getDay());
      return Array.from({ length: 7 }, (_, i) => {
        const dt = new Date(sunday);
        dt.setDate(sunday.getDate() + i);
        return dt;
      });
    } else {
      const year = anchorDate.getFullYear();
      const month = anchorDate.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
    }
  };

  const visibleDates = getVisibleDates();
  const leadingBlanks =
    viewMode === 'month' && visibleDates.length > 0 ? visibleDates[0].getDay() : 0;
  const calendarRows =
    viewMode === 'week' ? 1 : Math.ceil((leadingBlanks + visibleDates.length) / 7);

  const handlePrev = () => {
    setAnchorDate((prev) => {
      if (viewMode === 'week') {
        const next = new Date(prev);
        next.setDate(prev.getDate() - 7);
        return next;
      }
      return new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
    });
  };

  const handleNext = () => {
    setAnchorDate((prev) => {
      if (viewMode === 'week') {
        const next = new Date(prev);
        next.setDate(prev.getDate() + 7);
        return next;
      }
      return new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
    });
  };

  const getPeriodLabel = (): string => {
    if (viewMode === 'month') {
      return anchorDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (visibleDates.length === 0) return '';
    const first = visibleDates[0];
    const last = visibleDates[visibleDates.length - 1];
    return `${first.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })} – ${last.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  };

  const todayKey = toDateKey(new Date());

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        minHeight: '100vh',
        height: { lg: '100dvh' },
        maxHeight: { lg: '100dvh' },
        overflow: { lg: 'hidden' },
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'grey.50',
        paddingTop: { xs: '84px', sm: '216px' },
        pb: { xs: 6, lg: 1.5 },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          width: '100%',
          px: { xs: 2, sm: 3 },
          py: { xs: 2, lg: 1.25 },
          flex: { lg: '1 1 auto' },
          minHeight: { lg: 0 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Welcome Banner */}
        <Box sx={{ display: { xs: 'block', lg: 'none' }, mb: { xs: 2, sm: 2.5 }, textAlign: { xs: 'center', md: 'left' }, flexShrink: 0 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
              lineHeight: 1.2,
              background: 'linear-gradient(45deg, #0D47A1 30%, #1976d2 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            My Schedule
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.75, mx: { xs: 'auto', md: 0 }, maxWidth: { xs: 360, md: 560 } }}
          >
            Your assigned delivery shifts for each day.
          </Typography>
        </Box>

        {/* Driver's Personal Calendar Schedule */}
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 2.5, lg: 1.5 },
            borderRadius: 3,
            flex: { lg: '1 1 auto' },
            minHeight: { lg: 0 },
            display: 'flex',
            flexDirection: 'column',
            overflow: { lg: 'hidden' },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, lg: 1 }, mb: { xs: 2, lg: 1 }, flexShrink: 0 }}>
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1 }}
              >
                <CalendarMonthIcon color="primary" /> My Delivery Schedule Calendar
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', lg: 'block' } }}>
                {currentUser.name}
              </Typography>
            </Box>

            <Box sx={{ width: '100%' }}>
              {/* Mobile toolbar */}
              <Box
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                  flexDirection: 'column',
                  gap: 1.25,
                }}
              >
                <Box sx={{ position: 'relative', textAlign: 'center' }}>
                  <Typography
                    fontWeight={800}
                    color="primary.dark"
                    sx={{
                      fontSize: '1.15rem',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                    }}
                  >
                    {getPeriodLabel()}
                    <CalendarMonthIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  </Typography>
                  <Box
                    component="input"
                    type="month"
                    aria-label="Jump to month"
                    value={`${anchorDate.getFullYear()}-${String(
                      anchorDate.getMonth() + 1
                    ).padStart(2, '0')}`}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (!e.target.value) return;
                      const [y, m] = e.target.value.split('-');
                      setAnchorDate(new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1));
                    }}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      opacity: 0,
                      cursor: 'pointer',
                      width: '100%',
                      height: '100%',
                      border: 0,
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={handlePrev}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<TodayIcon />}
                    onClick={() => {
                      setAnchorDate(new Date());
                      setTimeout(() => {
                        const el = document.getElementById(
                          `driver-calendar-day-${toDateKey(new Date())}`
                        );
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 80);
                    }}
                    sx={{ fontWeight: 700, px: 2, minWidth: 110 }}
                  >
                    Today
                  </Button>
                  <IconButton
                    size="small"
                    onClick={handleNext}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  fullWidth
                  value={viewMode}
                  onChange={(_, val) => {
                    if (val) setViewMode(val);
                  }}
                >
                  <ToggleButton value="month" sx={{ fontWeight: 700, py: 0.85 }}>
                    Month
                  </ToggleButton>
                  <ToggleButton value="week" sx={{ fontWeight: 700, py: 0.85 }}>
                    Week
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Desktop / tablet toolbar */}
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  pt: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={handlePrev}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<TodayIcon />}
                    onClick={() => {
                      setAnchorDate(new Date());
                      setTimeout(() => {
                        const el = document.getElementById(
                          `driver-calendar-day-${toDateKey(new Date())}`
                        );
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 80);
                    }}
                    sx={{ fontWeight: 600 }}
                  >
                    Today
                  </Button>
                  <IconButton
                    size="small"
                    onClick={handleNext}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    color="primary.dark"
                    sx={{ ml: 0.5, whiteSpace: 'nowrap' }}
                  >
                    {getPeriodLabel()}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
                  <TextField
                    type="month"
                    size="small"
                    label="Jump to month"
                    value={`${anchorDate.getFullYear()}-${String(
                      anchorDate.getMonth() + 1
                    ).padStart(2, '0')}`}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const [y, m] = e.target.value.split('-');
                      setAnchorDate(new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1));
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 170 }}
                  />

                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={viewMode}
                    onChange={(_, val) => {
                      if (val) setViewMode(val);
                    }}
                    sx={{ height: 40 }}
                  >
                    <ToggleButton
                      value="month"
                      sx={{ fontWeight: 700, px: 1.75, textTransform: 'none' }}
                    >
                      Month
                    </ToggleButton>
                    <ToggleButton
                      value="week"
                      sx={{ fontWeight: 700, px: 1.75, textTransform: 'none' }}
                    >
                      Week
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            </Box>
          </Box>

          {viewMode === 'month' && (
            <Box
              sx={{
                display: { xs: 'none', lg: 'grid' },
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.75,
                mb: 0.5,
                flexShrink: 0,
              }}
            >
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <Typography
                  key={day}
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  align="center"
                >
                  {day}
                </Typography>
              ))}
            </Box>
          )}

          <Box
            sx={{
              flex: { lg: '1 1 auto' },
              minHeight: { lg: 0 },
              overflow: { lg: viewMode === 'week' ? 'auto' : 'hidden' },
              display: 'grid',
              alignContent: { lg: viewMode === 'week' ? 'start' : 'stretch' },
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
                lg: 'repeat(7, 1fr)',
              },
              gridTemplateRows: {
                lg: viewMode === 'month' ? `repeat(${calendarRows}, minmax(0, 1fr))` : 'auto',
              },
              gap: { xs: 1.5, lg: 0.75 },
            }}
          >
            {visibleDates.map((dateObj) => {
              const dateKey = toDateKey(dateObj);
              const isToday = dateKey === todayKey;
              const dayName = getDayOfWeekName(dateObj);
              const shortDateStr = formatShortCalendarDateLabel(dateObj);
              const shift = getScheduleShiftForDate(mySchedule, dateObj);
              const windowKeys = parseShiftWindows(shift);
              const activeWindowCount = windowKeys.filter(
                (wk) => !isScheduleShiftMarkedOff(mySchedule, dateObj, wk)
              ).length;
              const isScheduled = activeWindowCount > 0;

              return (
                <Paper
                  key={dateKey}
                  id={`driver-calendar-day-${dateKey}`}
                  variant="outlined"
                  sx={{
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: { lg: viewMode === 'month' ? '100%' : 'auto' },
                    minHeight: { lg: 0 },
                    borderColor: isToday
                      ? '#0D47A1'
                      : isScheduled
                      ? 'primary.light'
                      : 'divider',
                    borderWidth: isToday ? 2 : 1,
                    bgcolor: isScheduled ? 'background.paper' : 'grey.50',
                    gridColumn:
                      viewMode === 'month' && dateObj.getDate() === 1
                        ? { lg: dateObj.getDay() + 1 }
                        : undefined,
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': {
                      boxShadow: isScheduled ? '0 4px 12px rgba(25, 118, 210, 0.12)' : 'none',
                    },
                  }}
                >
                  {/* Day Header */}
                  <Box
                    sx={{
                      px: { xs: 1.5, lg: 1 },
                      py: { xs: 1, lg: 0.5 },
                      bgcolor: isToday
                        ? '#0D47A1'
                        : isScheduled
                        ? 'primary.main'
                        : 'grey.300',
                      color: isScheduled || isToday ? 'white' : 'text.secondary',
                      display: 'flex',
                      flexDirection: { xs: 'row', lg: viewMode === 'month' ? 'row' : 'column' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'center', lg: viewMode === 'month' ? 'center' : 'flex-start' },
                      gap: 0.25,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={800}
                        sx={{ whiteSpace: 'nowrap', lineHeight: 1.2 }}
                      >
                        {shortDateStr}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 600,
                          opacity: 0.9,
                          fontSize: '0.72rem',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2,
                        }}
                      >
                        {dayName}
                      </Typography>
                    </Box>
                    {windowKeys.length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          opacity: isScheduled ? 0.9 : 0.85,
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2,
                        }}
                      >
                        {activeWindowCount === 0
                          ? 'Marked (off)'
                          : `${activeWindowCount} ${activeWindowCount === 1 ? 'Shift' : 'Shifts'}`}
                      </Typography>
                    )}
                  </Box>

                  {/* Day Windows Body */}
                  <Box
                    sx={{
                      p: { xs: 1.5, lg: 0.75 },
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      flexGrow: 1,
                      minHeight: 0,
                      overflow: { lg: windowKeys.length > 0 && viewMode === 'month' ? 'auto' : 'hidden' },
                      justifyContent: windowKeys.length > 0 ? 'flex-start' : 'center',
                      alignItems: windowKeys.length > 0 ? 'stretch' : 'center',
                    }}
                  >
                    {windowKeys.length > 0 ? (
                      windowKeys.map((wk) => {
                        const w = DELIVERY_WINDOWS.find((dw) => dw.key === wk);
                        const isShiftMarkedOff = isScheduleShiftMarkedOff(
                          mySchedule,
                          dateObj,
                          wk
                        );
                        return (
                          <Box
                            key={wk}
                            sx={{
                              p: 1,
                              borderRadius: 1.5,
                              bgcolor: isShiftMarkedOff
                                ? 'grey.200'
                                : 'rgba(25, 118, 210, 0.07)',
                              border: isShiftMarkedOff
                                ? '1px solid rgba(0, 0, 0, 0.15)'
                                : '1px solid rgba(25, 118, 210, 0.2)',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 0.25,
                              }}
                            >
                              {getWindowIcon(wk)}
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color={isShiftMarkedOff ? 'text.secondary' : 'primary.dark'}
                                sx={{ lineHeight: 1.2 }}
                              >
                                {w ? `${w.label}${isShiftMarkedOff ? ' (off)' : ''}` : wk}
                              </Typography>
                            </Box>
                            {w && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', fontSize: '0.7rem', pl: 2.5 }}
                              >
                                {w.timeRange}
                              </Typography>
                            )}
                          </Box>
                        );
                      })
                    ) : (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            display: { xs: 'none', lg: viewMode === 'month' ? 'block' : 'none' },
                            color: 'text.secondary',
                            fontWeight: 600,
                          }}
                        >
                          Off Duty
                        </Typography>
                        <Chip
                          size="small"
                          label="Off Duty"
                          variant="outlined"
                          sx={{
                            color: 'text.secondary',
                            borderColor: 'grey.300',
                            display: { xs: 'inline-flex', lg: viewMode === 'month' ? 'none' : 'inline-flex' },
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Paper>

        {/* Back to Top Floating Button */}
        <Zoom in={showBackToTop}>
          <Fab
            color="primary"
            variant="extended"
            size="medium"
            onClick={scrollToTop}
            aria-label="Back to top"
            sx={{
              position: 'fixed',
              bottom: { xs: 20, sm: 28 },
              right: { xs: 20, sm: 32 },
              zIndex: 1200,
              fontWeight: 700,
              textTransform: 'none',
              px: 2.5,
              boxShadow: '0 6px 20px rgba(13, 71, 161, 0.35)',
              background: 'linear-gradient(90deg, #0D47A1 0%, #1976d2 100%)',
            }}
          >
            <KeyboardArrowUpIcon sx={{ mr: 0.5 }} />
            Back to Top
          </Fab>
        </Zoom>
      </Container>
    </Box>
  );
}
